import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();

  // Get the redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/';
  const pendingCartItem = location.state?.pendingCartItem;

  const { username, email, password, confirmPassword } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear any previous errors when the user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const result = await register({ username, email, password });
      
      if (result.success) {
        // If there's a pending cart item, add it to cart
        if (pendingCartItem) {
          const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
          const existingItemIndex = cartItems.findIndex(cartItem => 
            cartItem.id === pendingCartItem.id && cartItem.frameOption === pendingCartItem.frameOption
          );
          
          if (existingItemIndex !== -1) {
            cartItems[existingItemIndex].quantity += pendingCartItem.quantity;
            cartItems[existingItemIndex].totalPrice = 
              cartItems[existingItemIndex].price * cartItems[existingItemIndex].quantity;
          } else {
            cartItems.push(pendingCartItem);
          }
          
          localStorage.setItem('cartItems', JSON.stringify(cartItems));
          window.dispatchEvent(new Event('cartUpdated'));
        }
        
        // Get user data from localStorage to check role
        const userData = JSON.parse(localStorage.getItem('user'));
        
        // Prevent admin users from registering through regular registration
        if (userData?.role === 'admin') {
          setError('Admin accounts cannot be created through registration.');
          // Logout admin user
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return;
        }
        
        // Redirect to intended destination
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-arabic font-bold text-primary">
            <span className="text-accent">Tafsir</span> Art
          </Link>
          <h2 className="mt-2 text-2xl font-bold text-gray-800">Create Account</h2>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label 
              htmlFor="username" 
              className="block text-gray-700 font-medium mb-2"
            >
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 bg-white"
              required
              placeholder="johndoe"
            />
          </div>

          <div className="mb-4">
            <label 
              htmlFor="email" 
              className="block text-gray-700 font-medium mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 bg-white"
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="mb-4">
            <label 
              htmlFor="password" 
              className="block text-gray-700 font-medium mb-2"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 bg-white"
              required
              placeholder="********"
              minLength={6}
            />
          </div>

          <div className="mb-6">
            <label 
              htmlFor="confirmPassword" 
              className="block text-gray-700 font-medium mb-2"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 bg-white"
              required
              placeholder="********"
              minLength={6}
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primary-dark font-medium">
              Sign in here
            </Link>
          </p>
          <p className="mt-2">
            <Link to="/" className="text-primary hover:text-primary-dark">
              Back to Home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
